import { services } from 'services/base';
import { CloseAllErrorService, UnsubscribeMethod } from 'types';
import { upperCamelCase } from 'utils/helpers';

export type GetQueueItemsService<Value> = () => Value[];
export type GetQueueItemService<Value> = () => Value | undefined;
export type AddQueueItemService<Value> = (value: Value) => void;
export type RemoveQueueItemService = () => void;

export type OnQueueChangeServiceCallback<Value> = (value: Value) => void;
export type OnQueueItemChangeService<Value> = (callback: OnQueueChangeServiceCallback<Value>) => UnsubscribeMethod;
export type OnQueueChangeService<Value> = (callback: OnQueueChangeServiceCallback<Value[]>) => UnsubscribeMethod;

type RegisterQueueServiceMethods = {
  addItem?: string;
  removeItem?: string;
  removeAll?: string;
  getItem?: string;
  getQueue?: string;
  onItemChange?: string;
  onQueueChange?: string;
};

type RegisterQueueOptions<StateKey extends string, Value extends unknown> = {
  state: { [key in StateKey]: Value[] };
  stateKey: StateKey;
  initValue?: Value[];
  servicePrefix: string;
  serviceMethods?: RegisterQueueServiceMethods;
};

type RegisterQueueResult = {
  notifyQueueItemChangeAction: string;
  updateQueueAction: string;
};

const registerQueue = <StateKey extends string, Value extends unknown>({
  state,
  stateKey,
  initValue,
  servicePrefix,
  serviceMethods: {
    addItem = 'addItem',
    removeItem = 'removeItem',
    removeAll = 'removeAll',
    getItem = 'getItem',
    getQueue = 'getQueue',
    onItemChange = 'onItemChange',
    onQueueChange = 'onQueueChange',
  } = {},
}: RegisterQueueOptions<StateKey, Value>): RegisterQueueResult => {
  const actionPrefix = `${servicePrefix.split('.').map(upperCamelCase).join(':')}`;
  const notifyQueueItemChangeAction = `${actionPrefix}:${upperCamelCase(onItemChange)})`;
  const updateQueueAction = `${actionPrefix}:${upperCamelCase(onQueueChange)}`;
  if (initValue !== undefined) {
    state[stateKey] = initValue;
  }

  const { registerAction, clearAction, registerService, executeAction } = services;
  registerService<GetQueueItemService<Value>>(`${servicePrefix}.${getItem}`, () => {
    return state[stateKey][0];
  });

  registerService<GetQueueItemsService<Value>>(`${servicePrefix}.${getQueue}`, () => state[stateKey]);

  registerAction<Value[]>(
    updateQueueAction,
    (newValues) => {
      state[stateKey] = newValues;
      executeAction(notifyQueueItemChangeAction, newValues[0]);
    },
    { order: 0 },
  );

  registerService<AddQueueItemService<Value>>(`${servicePrefix}.${addItem}`, (newValue) => {
    const values = state[stateKey];
    executeAction(updateQueueAction, [...values, newValue]);
  });

  registerService<RemoveQueueItemService>(`${servicePrefix}.${removeItem}`, () => {
    const values = state[stateKey];
    if (!values.length) {
      return;
    }
    executeAction(updateQueueAction, values.slice(1));
  });

  registerService<CloseAllErrorService>(`${servicePrefix}.${removeAll}`, () => {
    executeAction(updateQueueAction, []);
  });

  registerService<OnQueueItemChangeService<Value>>(`${servicePrefix}.${onItemChange}`, (callback) => {
    registerAction(notifyQueueItemChangeAction, callback, { order: 1 });
    return () => clearAction(notifyQueueItemChangeAction, callback);
  });

  registerService<OnQueueChangeService<Value>>(`${servicePrefix}.${onQueueChange}`, (callback) => {
    registerAction(updateQueueAction, callback, { order: 1 });
    return () => clearAction(updateQueueAction, callback);
  });

  return { notifyQueueItemChangeAction, updateQueueAction };
};

export default registerQueue;
